import React, { useMemo } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import usePrivacyMaskValue from 'app-components/hooks/privacyMode/usePrivacyMaskValue';
import useFormatAmount from 'app-components/hooks/privacyMode/useFormatAmount';

import { buildPieChartOption, getSafeColor } from 'utils/chart-options';
import type { PieChartDatum } from 'utils/chart-options';
import EChartsBase from '../EChartsBase';
import useEChartsTheme from '../useEChartsTheme';
import type { ChartPreviewRequest } from './chartPreviewUtils';

type Props = {
  chart: ChartPreviewRequest;
  height?: number | string;
  width?: number | string;
};

const DEFAULT_HEIGHT = 240;

// Compact pie preview for the AI chat card, deliberately not LegendPieChart:
// - LegendPieChart's legend items (18px font, 17px color dot, breadcrumb nav, "Everything else"
//   bucketing/show-all toggle) assume report-width containers and drill-down data (originalRow,
//   categoryId, breadcrumbs). None of that applies to a static AI-generated chart, and its column
//   floors (chart minWidth 220, legend column minWidth 135) are still too large for a chat bubble.
// - This renders the pie on top and a single-column, tightly-spaced legend stacked below it,
//   sized for the narrow preview.
// The pie itself still stays visually in sync with LegendPieChart: both call the shared
// `buildPieChartOption` builder (utils/chart-options/pie) and the shared `getSafeColor` helper, so
// slice styling, tooltip layout, and color-cycling changes made there apply here automatically.
const PieChartPreview = ({ chart, height, width }: Props): React.JSX.Element | null => {
  const theme = useTheme();
  const echartsTheme = useEChartsTheme();
  const formatAmount = useFormatAmount();
  const privacyMask = usePrivacyMaskValue();
  const colors = theme.palette.data.sequence;
  const hasFixedHeight = typeof height !== 'undefined';

  const valueField = chart.valueFields[0];
  const rows = useMemo(
    () =>
      chart.data.flatMap((row) => {
        const total = row[valueField];
        const category = row[chart.categoryField];
        return typeof total === 'number' ? [{ category: String(category ?? ''), total }] : [];
      }),
    [chart.data, chart.categoryField, valueField],
  );

  const option = useMemo(() => {
    const pieData: PieChartDatum[] = rows.map((row, index) => ({
      name: row.category,
      value: Math.abs(row.total),
      color: getSafeColor(colors, index) ?? '#cccccc',
      tooltipAmount: String(privacyMask(formatAmount(row.total, 'USD', '0,0.00'))),
    }));

    return buildPieChartOption(pieData, {
      theme: echartsTheme,
      formatAmount,
      privacyMask,
      animated: false,
      innerRadiusPercent: 35,
    });
  }, [rows, colors, echartsTheme, formatAmount, privacyMask]);

  if (!rows.length) return null;

  return (
    <Box
      sx={{
        width: width ?? '100%',
        height: hasFixedHeight ? height : 'auto',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: hasFixedHeight ? 'auto' : DEFAULT_HEIGHT,
          flex: hasFixedHeight ? '1 1 auto' : '0 0 auto',
          minHeight: hasFixedHeight ? 120 : undefined,
        }}
      >
        <EChartsBase option={option} height='100%' renderer='canvas' testId='echart-ai-pie-chart-preview' />
      </Box>
      <Stack
        spacing={0.5}
        sx={{
          minWidth: 0,
          flexShrink: 0,
          mt: 1,
          maxHeight: hasFixedHeight ? '40%' : 'none',
          overflowY: hasFixedHeight ? 'auto' : 'visible',
        }}
      >
        {rows.map((row, index) => (
          <Stack key={row.category} direction='row' spacing={1} alignItems='center' sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: getSafeColor(colors, index) ?? '#cccccc',
                flexShrink: 0,
              }}
            />
            <Typography
              variant='captionRegular'
              noWrap
              sx={{ flex: 1, minWidth: 0, color: theme.palette.text.primary }}
            >
              {row.category}
            </Typography>
            <Typography variant='captionBold' noWrap sx={{ flexShrink: 0 }}>
              {privacyMask(formatAmount(row.total, 'USD', '0,0'))}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export default PieChartPreview;
